import { ReactNode, useId } from 'react';
import { useFieldContext } from '@/lib/form.ts';
import { Input } from '@/common/components/ui/input';
import { Label } from '@/common/components/ui/label';
import { COMMON_VALIDATION_ERRORS, ZodIssueMap } from '@/common/form/utils.ts';
import { t } from '@lingui/core/macro';
import { FormFieldErrors } from '@/common/components/form-field-errors/FormFieldErrors.tsx';
import { useShouldShowFieldErrors } from '@/common/hooks/useShouldShowFieldErrors.ts';
import { Loader2 } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@/lib/utils';

const zodIssueMap: ZodIssueMap = {
  invalidType: COMMON_VALIDATION_ERRORS.required,
  tooSmall: (issue) => {
    const minimum = issue.minimum as number;
    return t`Minimum length is ${minimum}`;
  },
  tooBig: (issue) => {
    const maximum = issue.maximum as number;
    return t`Maximum length is ${maximum}`;
  },
};

export interface FormTextFieldProps {
  label?: ReactNode;
  labelAddon?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  type?: string;
  errorMessage?: string;
  isLoading?: boolean;
}

export const FormTextField = ({
  fullWidth = true,
  errorMessage,
  isLoading,
  label,
  labelAddon,
  placeholder,
  disabled,
  required,
  type = 'text',
}: FormTextFieldProps) => {
  const field = useFieldContext<string | null>();
  const { shouldShowFieldErrors, onBlur } = useShouldShowFieldErrors(!!errorMessage);
  const { t } = useLingui();
  const id = useId();

  const handleBlur = () => {
    field.handleBlur();
    onBlur();
  };

  return (
    <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
      {(label || labelAddon) && (
        <div className="flex items-center justify-between">
          <Label htmlFor={id} className={cn(shouldShowFieldErrors && 'text-destructive')}>
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </Label>
          {labelAddon}
        </div>
      )}
      <div className="relative">
        <Input
          id={id}
          type={type}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value || null)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-invalid={shouldShowFieldErrors || undefined}
          className={cn(isLoading && 'pr-8')}
        />
        {isLoading && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <Loader2
              className="h-4 w-4 animate-spin text-muted-foreground"
              aria-label={t`Loading`}
              role="progressbar"
            />
          </div>
        )}
      </div>
      {shouldShowFieldErrors && (
        <div className="text-sm text-destructive">
          <FormFieldErrors zodIssueMap={zodIssueMap} />
          {errorMessage && (
            <span data-testid={`${field.name}-error`} className="block">
              {errorMessage}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
